import { LkReactMenu, LkReactMenuId, LkRoleId, Permission, PermissionAction } from "generated/openApi";
import { ReactNode } from "react";

import AppsIcon from "@mui/icons-material/Apps";
import HomeIcon from "@mui/icons-material/Home";
import LocalAtmIcon from "@mui/icons-material/LocalAtm";
import SettingsIcon from "@mui/icons-material/Settings";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import SchoolIcon from "@mui/icons-material/School";
import AssessmentIcon from "@mui/icons-material/Assessment";
import StorageIcon from "@mui/icons-material/Storage";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import LocalLibraryIcon from "@mui/icons-material/LocalLibrary";

import { isAuthorized } from "services";
import { homePageRoute } from "pages/HomePage/HomePage";
import { contributorListPageRoute } from "pages/match-main/contributors/ContributorListPage/ContributorListPage";
import { contributorMatchListPageRoute } from "pages/match-main/contributor-matches/ContributorMatchListPage/ContributorMatchListPage";
import { nonPersonnelListPageRoute } from "pages/match-main/nonPersonnels/NonPersonnelListPage/NonPersonnelListPage";
import { matchReportPageRoute } from "pages/match-main/reports/MatchReportPage/MatchReportPage";
import { contributorMatchServiceListPageRoute } from "pages/match-utility/contributor-match-services/ContributorMatchServiceListPage/ContributorMatchServiceListPage";
import { suppliesDetailListPageRoute } from "pages/match-utility/supplies-details/SuppliesDetailListPage/SuppliesDetailListPage";
import { partnerListPageRoute } from "pages/match-utility/partners/PartnerListPage/PartnerListPage";
import { userListPageRoute } from "pages/users/UserListPage/UserListPage";
import { dataCenterFileListPageRoute } from "pages/data-center/dataCenterFiles/DataCenterFileListPage/DataCenterFileListPage";
import { developerListPageRoute } from "pages/cobro/developers/DeveloperListPage/DeveloperListPage";
import { cobroSurveyExportPageRoute } from "pages/cobro/survey-export/CobroSurveyExportPage";
import { surveyExportPageRoute } from "pages/surveys/survey-export/SurveyExportPage";
import { surveyResponseListPageRoute } from "pages/cobro/survey-responses/SurveyResponseListPage/SurveyResponseListPage";
import { studentListPageRoute } from "pages/students/students/StudentListPage/StudentListPage";
import { studentServiceListPageRoute } from "pages/students/student-services/StudentServiceListPage/StudentServiceListPage";
import { studentEnrollmentListPageRoute } from "pages/students/student-enrollments/StudentEnrollmentListPage/StudentEnrollmentListPage";
import { studentReportPageRoute } from "pages/reports/StudentReportPage/StudentReportPage";
import { exportPageRoute } from "pages/reports/ExportPage/ExportPage";
import { studentGroupListPageRoute } from "pages/students/student-groups/StudentGroupListPage/StudentGroupListPage";
import { studentServiceTemplateListPageRoute } from "pages/students/student-service-templates/StudentServiceTemplateListPage/StudentServiceTemplateListPage";
import { customDataModelFieldListPageRoute } from "pages/system/custom-data-model-fields/CustomDataModelFieldListPage/CustomDataModelFieldListPage";
import { surveyListPageRoute } from "pages/cobro/surveys/SurveyListPage/SurveyListPage";
import { applicationListPageRoute } from "pages/cobro/applications/ApplicationListPage/ApplicationListPage";
import { utilityLayoutRoutePath } from "pages/system/utility/_layouts/UtilityLayout";
import { dashboardReportPageRoute } from "pages/reports/DashboardReportPage/DashboardReportPage";
import { studentApplicationListPageRoute } from "pages/students/student-applications/StudentApplicationListPage/StudentApplicationListPage";
import { aprPageRoute } from "pages/ed-reports/AprPage/AprPage";
import { objectiveReportPageRoute } from "pages/reports/ObjectiveReportPage/ObjectiveReportPage";
import { aprTrioPageRoute } from "pages/ed-reports/AprTrio/AprTrioPage";
import { aprEtsPageRoute } from "pages/ed-reports/AprEts/AprEtsPage";
import { staffListPageRoute } from "pages/staff/staff/StaffListPage/StaffListPage";
import { staffServiceListPageRoute } from "pages/staff/staff-services/StaffServiceListPage/StaffServiceListPage";
import { staffReportPageRoute } from "pages/reports/StaffReportPage/StaffReportPage";
import { textingConversationsPageRoute } from "pages/texting/TextingConversationsPage/TextingConversationsPage";
import { studentFileListPageRoute } from "pages/students/student-files/StudentFileListPage/StudentFileListPage";

const COBRO_SUFFIX = "(Cobro)";

export type NavMenuItem = {
  title: string;
  subMenuItems: NavSubMenuItem[];
  icon?: ReactNode;
  expanded?: boolean;
};

export type NavSubMenuItem = {
  title: string;
  path?: string;
  menuId?: LkReactMenuId; // this will be used to check if this menu exists in TenantInfo: if empty then always show
  permission?: Permission; // this will be used to check if permission exists in current user's rolePermissions: if empty then always show
  roleId?: LkRoleId; // this will be used to check if the current role satisfies: this should only be used for special Cobro menus
  subSubMenuItems?: NavSubSubMenuItem[] | undefined;
  expanded?: boolean;
};

export type NavSubSubMenuItem = {
  title: string;
  path: string;
};

export const initialNavMenuItems: NavMenuItem[] = [
  {
    title: "Home",
    icon: <HomeIcon />,
    subMenuItems: [
      {
        title: homePageRoute.handle,
        path: homePageRoute.getPath(),
      },
    ],
  },
  {
    title: "Students",
    icon: <SchoolIcon />,
    subMenuItems: [
      {
        title: "Profiles",
        path: studentListPageRoute.getPath(),
        menuId: LkReactMenuId.Students_Students,
        permission: Permission.Student,
      },
      {
        title: studentEnrollmentListPageRoute.handle,
        path: studentEnrollmentListPageRoute.getPath(),
        menuId: LkReactMenuId.Students_Enrollments,
        permission: Permission.StudentEnrollment,
      },
      {
        title: studentServiceListPageRoute.handle,
        path: studentServiceListPageRoute.getPath(),
        menuId: LkReactMenuId.Students_Services,
        permission: Permission.StudentService,
      },
      {
        title: studentServiceTemplateListPageRoute.handle,
        path: studentServiceTemplateListPageRoute.getPath(),
        menuId: LkReactMenuId.Students_QrCode,
        permission: Permission.StudentServiceTemplate,
      },

      {
        title: studentGroupListPageRoute.handle,
        path: studentGroupListPageRoute.getPath(),
        menuId: LkReactMenuId.Students_Groups,
        permission: Permission.StudentGroup,
      },
      {
        title: studentApplicationListPageRoute.handle,
        path: studentApplicationListPageRoute.getPath(),
        menuId: LkReactMenuId.Students_Applications,
        permission: Permission.StudentApplication,
      },
      // {
      //   title: studentFileListPageRoute.handle,
      //   path: studentFileListPageRoute.getPath(),
      //   menuId: LkReactMenuId.Students_Applications,
      //   permission: Permission.StudentApplication,
      // },
    ],
  },
  {
    title: "Staff",
    icon: <LocalLibraryIcon />,
    subMenuItems: [
      {
        title: "Profiles",
        path: staffListPageRoute.getPath(),
        menuId: LkReactMenuId.Staff_Staff,
        permission: Permission.Staff,
      },
      {
        title: staffServiceListPageRoute.handle,
        path: staffServiceListPageRoute.getPath(),
        menuId: LkReactMenuId.Staff_Services,
        permission: Permission.StaffService,
      },
    ],
  },
  {
    title: "Match",
    icon: <LocalAtmIcon />,
    subMenuItems: [
      {
        title: contributorListPageRoute.handle,
        path: contributorListPageRoute.getPath(),
        menuId: LkReactMenuId.Match_Contributors,
        permission: Permission.Contributor,
      },
      {
        title: contributorMatchListPageRoute.handle,
        path: contributorMatchListPageRoute.getPath(),
        menuId: LkReactMenuId.Match_TimeAndEfforts,
        permission: Permission.ContributorMatch,
      },
      {
        title: nonPersonnelListPageRoute.handle,
        path: nonPersonnelListPageRoute.getPath(),
        menuId: LkReactMenuId.Match_NonPersonnels,
        permission: Permission.NonPersonnel,
      },
      {
        title: matchReportPageRoute.handle,
        path: matchReportPageRoute.getPath(),
        menuId: LkReactMenuId.Match_Reports,
        permission: Permission.MatchReport,
      },
      // {
      //   title: "Exports",
      //   path: matchExportPageRoute.getPath(),
      //   menuId: LkReactMenuId.Match_Exports,
      //   permission: Permission.MatchExport,
      // },
      {
        title: "Utility",
        menuId: LkReactMenuId.Match_Utility,
        permission: Permission.MatchUtility,
        subSubMenuItems: [
          {
            title: partnerListPageRoute.handle,
            path: partnerListPageRoute.getPath(),
          },
          {
            title: contributorMatchServiceListPageRoute.handle,
            path: contributorMatchServiceListPageRoute.getPath(),
          },
          {
            title: suppliesDetailListPageRoute.handle,
            path: suppliesDetailListPageRoute.getPath(),
          },
        ],
      },
      {
        title: userListPageRoute.handle,
        path: userListPageRoute.getPath(),
        menuId: LkReactMenuId.Match_Users,
        permission: Permission.User,
      },
    ],
  },
  {
    title: "Reports",
    icon: <AssessmentIcon />,
    subMenuItems: [
      {
        title: studentReportPageRoute.handle,
        path: studentReportPageRoute.getPath(),
        menuId: LkReactMenuId.Reports_Students,
        permission: Permission.Report,
      },
      {
        title: staffReportPageRoute.handle,
        path: staffReportPageRoute.getPath(),
        menuId: LkReactMenuId.Reports_Staff,
        permission: Permission.Report,
      },
      {
        title: exportPageRoute.handle,
        path: exportPageRoute.getPath(),
        menuId: LkReactMenuId.Reports_Exports,
        permission: Permission.SystemExport,
      },
      {
        title: dashboardReportPageRoute.handle,
        path: dashboardReportPageRoute.getPath(),
        menuId: LkReactMenuId.Reports_Dashboard,
        permission: Permission.Report,
      },
      {
        title: objectiveReportPageRoute.handle,
        path: objectiveReportPageRoute.getPath(),
        menuId: LkReactMenuId.Reports_Objectives,
        permission: Permission.Report,
      },
    ],
  },
  {
    title: "ED Reports",
    icon: <StorageIcon />,
    subMenuItems: [
      {
        title: aprPageRoute.handle,
        path: aprPageRoute.getPath(),
        menuId: LkReactMenuId.EdReports_Apr,
        permission: Permission.EdReport,
      },
      {
        title: aprTrioPageRoute.handle,
        path: aprTrioPageRoute.getPath(),
        menuId: LkReactMenuId.EdReports_AprSSS,
        permission: Permission.AprSss,
      },
      {
        title: aprEtsPageRoute.handle,
        path: aprEtsPageRoute.getPath(),
        menuId: LkReactMenuId.EdReports_AprETS,
        permission: Permission.AprEts,
      },
    ],
  },
  {
    title: "System",
    icon: <SettingsIcon />,
    subMenuItems: [
      {
        title: "Utility",
        path: utilityLayoutRoutePath,
        permission: Permission.SystemUtility,
      },
      {
        title: userListPageRoute.handle,
        path: userListPageRoute.getPath(),
        permission: Permission.User,
      },
      {
        title: applicationListPageRoute.handle,
        path: applicationListPageRoute.getPath(),
        permission: Permission.SystemUtility,
      },
      {
        title: customDataModelFieldListPageRoute.handle + COBRO_SUFFIX,
        roleId: LkRoleId.Cobro, // TODO: remove and needs menuId
        path: customDataModelFieldListPageRoute.getPath(),
        permission: Permission.SystemUtility,
      },
    ],
  },
  {
    title: "Data Center",
    icon: <InsertDriveFileIcon />,
    subMenuItems: [
      {
        title: dataCenterFileListPageRoute.handle,
        path: dataCenterFileListPageRoute.getPath(),
        menuId: LkReactMenuId.DataCenter_DataCenterFiles,
        permission: Permission.DataCenterFile,
      },
    ],
  },
  {
    title: "Cobro",
    icon: <AppsIcon />,
    subMenuItems: [
      {
        title: developerListPageRoute.handle,
        path: developerListPageRoute.getPath(),
        roleId: LkRoleId.Cobro,
      },
      {
        title: surveyListPageRoute.handle,
        path: surveyListPageRoute.getPath(),
        roleId: LkRoleId.Cobro,
      },
      {
        title: cobroSurveyExportPageRoute.handle,
        path: cobroSurveyExportPageRoute.getPath(),
        roleId: LkRoleId.Cobro,
      },
    ],
  },
  {
    title: "Forms",
    icon: <AnalyticsIcon />,
    subMenuItems: [
      {
        title: surveyResponseListPageRoute.handle,
        path: surveyResponseListPageRoute.getPath(),
        permission: Permission.Form,
      },
      {
        title: surveyExportPageRoute.handle,
        path: surveyExportPageRoute.getPath(),
        permission: Permission.Form,
      },
    ],
  },
  {
    title: "Texting",
    icon: <AnalyticsIcon />,
    subMenuItems: [
      {
        menuId: LkReactMenuId.Texting_Conversations,
        title: textingConversationsPageRoute.handle,
        path: textingConversationsPageRoute.getPath(),
        permission: Permission.Texting,
      },
    ],
  },
];

// here we want to filter out the menu that the current user cannot see based on their permission and tenant setting
export const getNavMenuItems = (reactMenus: LkReactMenu[] | undefined, roleId: number | undefined): NavMenuItem[] => {
  let filteredMenuItems: NavMenuItem[] = [];

  for (let i: number = 0; i < initialNavMenuItems.length; i++) {
    let menuItem: NavMenuItem = initialNavMenuItems[i];
    let filteredSubMenuItems: NavSubMenuItem[] = [];

    for (let j: number = 0; j < menuItem.subMenuItems.length; j++) {
      let subMenuItem: NavSubMenuItem = menuItem.subMenuItems[j];

      if (
        !(
          (subMenuItem.permission && !isAuthorized(subMenuItem.permission, PermissionAction.View)) ||
          (subMenuItem.menuId && !reactMenus?.map((x) => x.id).includes(Number(subMenuItem.menuId))) ||
          (subMenuItem.roleId && Number(subMenuItem?.roleId) !== roleId)
        )
      ) {
        filteredSubMenuItems.push({
          title: subMenuItem.title,
          path: subMenuItem.path,
          menuId: subMenuItem.menuId,
          permission: subMenuItem.permission,
          subSubMenuItems: subMenuItem.subSubMenuItems,
        });
      }
    }

    if (filteredSubMenuItems.length > 0) {
      filteredMenuItems.push({
        title: menuItem.title,
        icon: menuItem.icon,
        expanded: menuItem.expanded,
        subMenuItems: filteredSubMenuItems,
      });
    }
  }

  return filteredMenuItems;
};
